/**
 * 進貨 Goods receipt docket contracts (S5.1) — Board D #5.
 *
 * Fixed decisions encoded here (holistic plan §S5.1):
 * - The receipt is a vendor DOCKET: `docketNumber` 貨單編號 (e.g. B40790056)
 *   is the paper docket identity, not a system GR sequence.
 * - `containerNumbers` 貨櫃號碼 is MULTI-order capable — modeled as an array
 *   pending Joe's numbering decision. On the seeded narrative it carries
 *   253061C, which doubles as Sterling's customer invoice number.
 * - `sealedConfirmed` 已封櫃 is a PRECONDITION: a docket can only post against
 *   quantities fixed at the shipment-batch seal (A3 裝櫃實際 actuals). Posting
 *   advances the received bucket and must NEVER rewrite the load balance —
 *   待出量 stays derived from the seal-fixed load axis.
 * - `invoiceMatchStatus` is the 會計對帳 receipt-vs-vendor-invoice amount
 *   match (3-way-match precursor, Board C/7). It is recomputed from the AP
 *   store at read — the docket never owns the invoice truth.
 *
 * The legacy `GoodsReceipt` shape in `./shipping.ts` is the codex-era seam the
 * OCC 收貨狀態 referenced lens still projects from; it retires when the
 * shipping seams migrate (S6.1). This module is the business contract.
 */

export type GoodsReceiptStatus = 'draft' | 'posted';

/** 會計對帳 status chip values — computed, never hand-set. */
export type InvoiceMatchStatus = 'unmatched' | 'matched' | 'variance';

export const INVOICE_MATCH_STATUS_ORDER: readonly InvoiceMatchStatus[] = [
  'matched',
  'variance',
  'unmatched',
] as const;

export interface GoodsReceiptLine {
  id: string;
  poLineId: string;
  productId: string;
  sku: string;
  productName: string;
  /** Received quantity on the docket — bounded by the sealed, unreceived qty. */
  qty: number;
  /** Docket unit price (defaults from the PO line purchase price). */
  price: number;
  /** qty × price, 2dp. */
  amount: number;
  note?: string | null;
}

/** 進貨單 — the goods receipt docket. */
export interface GoodsReceipt {
  id: string;
  /** 貨單編號 — vendor docket number, e.g. B40790056. */
  docketNumber: string;
  poId: string;
  poNumber: string;
  vendorId: number;
  vendorName: string;
  /** Shipment batch the sealed quantities came from (A3 feed), when known. */
  batchId?: string | null;
  /**
   * 貨櫃號碼 — multi-order capable, modeled as an array pending Joe.
   * Narrative: ['253061C'] = Sterling's customer invoice number (40呎).
   */
  containerNumbers: string[];
  /** 已封櫃 precondition — dockets only post against seal-fixed quantities. */
  sealedConfirmed: boolean;
  receiptDate: string;
  status: GoodsReceiptStatus;
  currency: string;
  /** Σ line amounts, 2dp — the receipt side of the 會計對帳 match. */
  totalAmount: number;
  /** Recomputed against the vendor-invoice store at read. */
  invoiceMatchStatus: InvoiceMatchStatus;
  lines: GoodsReceiptLine[];
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// A3 actual-at-loading feed (docket prefill)
// ---------------------------------------------------------------------------

export interface GoodsReceiptPrefillLine {
  poLineId: string;
  productId: string;
  sku: string;
  productName: string;
  /** A3 裝櫃實際 — the seal-fixed loaded quantity for this PO line. */
  qtyLoaded: number;
  /** Already posted on earlier dockets. */
  qtyReceived: number;
  /** max(0, qtyLoaded − qtyReceived) — the receivable remainder. */
  qtyOpenToReceive: number;
  /** PO line purchase price — the docket price default. */
  price: number;
}

/** GET /goods-receipts/prefill?poId= — the A3 actual-at-loading feed. */
export interface GoodsReceiptPrefill {
  poId: string;
  poNumber: string;
  vendorId: number;
  vendorName: string;
  currency: string;
  /** 已封櫃 — true when at least one sealed batch covers this PO. */
  sealedConfirmed: boolean;
  batchId: string | null;
  containerNumbers: string[];
  lines: GoodsReceiptPrefillLine[];
}

export interface CreateGoodsReceiptLineInput {
  poLineId: string;
  qty: number;
  /** Omit to default from the PO line purchase price. */
  price?: number;
  note?: string | null;
}

export interface CreateGoodsReceiptRequest {
  poId: string;
  /** 貨單編號 from the vendor docket. */
  docketNumber: string;
  receiptDate?: string;
  lines: CreateGoodsReceiptLineInput[];
}

// ---------------------------------------------------------------------------
// 會計對帳 — receipt vs vendor invoice amount match (Board C/7)
// ---------------------------------------------------------------------------

export interface ReceiptInvoiceMatchRow {
  receiptId: string;
  docketNumber: string;
  poId: string;
  poNumber: string;
  vendorId: number;
  vendorName: string;
  currency: string;
  receiptDate: string;
  /** Receipt side: docket total. */
  receiptAmount: number;
  vendorInvoiceId: string | null;
  vendorInvoiceNumber: string | null;
  /** Invoice side: vendor invoice total, null while unmatched. */
  invoiceAmount: number | null;
  /** invoiceAmount − receiptAmount, signed, null while unmatched. */
  variance: number | null;
  status: InvoiceMatchStatus;
}

/** GET /finance/receipt-invoice-match — read-only referenced projection. */
export interface ReceiptInvoiceMatchResponse {
  data: ReceiptInvoiceMatchRow[];
  syncedAt: string;
}

export interface GoodsReceiptListResponse {
  data: GoodsReceipt[];
}
